/* eslint-disable react-hooks/refs -- drag state intentionally lives in the shared ref-backed handler registry */
import { AnimatePresence, motion } from 'framer-motion';
import { Paperclip } from 'lucide-react';
import { useId } from 'react';

import { useDragHandlersRef } from './chat-input-context';
import { novaGradientSvgStop } from '../../design-system';

interface DragOverlayProps {
  className?: string;
}

/**
 * Drag overlay component that shows when files are being dragged over the input.
 * Reads drag state from drag handlers ref (populated by ChatAttachments if rendered).
 */
export function DragOverlay({ className }: DragOverlayProps) {
  const handlersRef = useDragHandlersRef();
  const isDragging = handlersRef.current.isDragging ?? false;

  return <DragOverlayView isDragging={isDragging} className={className} />;
}

export function DragOverlayView({ isDragging, className }: DragOverlayProps & { isDragging: boolean }) {
  const gradientId = useId().replaceAll(':', '');

  return (
    <AnimatePresence>
      {isDragging && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`absolute inset-0 z-50 flex items-center justify-center gap-3 rounded-[12px] bg-white p-4 ${className || ''}`}
        >
          <svg aria-hidden="true" className="pointer-events-none absolute inset-0 size-full">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="31%">
                {novaGradientSvgStop.aiBorderIcon.map(({ offset, stopColor }) => (
                  <stop key={offset} offset={offset} stopColor={stopColor} />
                ))}
              </linearGradient>
            </defs>
            <rect
              x="1"
              y="1"
              width="calc(100% - 2px)"
              height="calc(100% - 2px)"
              rx="11"
              pathLength="1"
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth="2"
              strokeDasharray="0.003125 0.003125"
            />
          </svg>
          <Paperclip size={24} />
          <div className="flex flex-col gap-1">
            <p className="text-base font-medium">Drop files here to attach</p>
            <p className="text-muted-foreground text-sm">Supports PDF, Office, email, ZIP, and image files</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
